<template>
  <!-- O relacionamento como associação, os dois lados na mesma tela (pedido do usuário: "indiferente
       editar por um lado ou pelo outro"). Aberta pelo espelho, "este lado" é o espelho e o outro é o
       dono — as mesmas caixas, na ordem de quem abriu. Em abas (ver o `PropertyTabs`), e em todas a
       mesma divisão por lado (pedido do usuário) -->
  <ontos-property-tabs ref="tabs" :validation="hasValidation" :invalid="invalidTabs">
    <template #property>
      <ontos-property-form-layout>
        <template #top>
          <ontos-select v-if="thisIsOwner && !fixedDestinationClass"
                        :items="ideas.map(i => ({value: i.alias, text: i.name}))"
                        :label="$tt('relationship-collection')" class="required" hide-details="auto"
                        :error-messages="destinationError" v-model="model.relationshipIdea"/>
          <div class="relationship-sides">
            <ontos-relationship-side-box v-for="(side, index) in sides" :key="side.key" :idea="side.idea" :here="index === 0">
              <!-- O espelho se liga e se desliga daqui só do lado do dono: o dono não se tira por esta
                   tela — tirá-lo apaga os vínculos gravados, que é a lixeira, com a revisão própria -->
              <ontos-pair-toggle v-if="side.togglable" :model-value="mirrorOn" v-bind="mirrorPair"
                                 :disabled="!!ownerModel.computed"
                                 :disabled-reason="ownerModel.computed ? $tt('relationship.mirror-needs-filled') : ''"
                                 @update:model-value="mirrorOn = $event">
                <ontos-relationship-side ref="sides" v-bind="side.fields"/>
              </ontos-pair-toggle>
              <ontos-relationship-side v-else ref="sides" v-bind="side.fields"/>
              <!-- A Cardinalidade de cada lado na caixa dele, e o resto do comportamento na do dono (ver o
                   `behaviorOf`). O "Vários valores" sai como evento: sem ouvi-lo, clicar nele não fazia nada -->
              <!-- O sentido da associação que sai deste lado é a frase da Cardinalidade dele (pedido do
                   usuário), no lugar de "Um valor só por item" -->
              <ontos-property-behavior v-if="side.behavior" ref="behavior" :model="side.behavior.model"
                                       :bindings="side.bindings" :draft="side.draft"
                                       :required-when-example="side.examples.requiredWhen"
                                       :kind="side.behavior.kind" :cardinality-hint="side.sentence"
                                       :compute-blocked="mirrorOn ? $tt('relationship.computed-needs-no-mirror') : ''"
                                       @multiple="side.behavior.model.multiple = $event"/>
              <!-- O dono calculado não tem a Cardinalidade de lá: a frase fica sozinha -->
              <div v-else-if="side.sentence" class="text-body-2 text-medium-emphasis">{{ side.sentence }}</div>
              <!-- O valor padrão só no dono, que é quem guarda os ids — o espelho é a lista do outro lado —, e
                   não no calculado, cujo valor vem da fórmula. Só com o destino: é dele que vêm os itens -->
              <ontos-property-default v-if="side.key === 'owner' && !side.model.computed && mirrorIdea" ref="default"
                                      :value="side.model.default" :draft="side.draft" :bindings="side.bindings"
                                      :result-label="formulaResultLabel" @input="side.model.default = $event"/>
            </ontos-relationship-side-box>
          </div>
          <!-- O relacionamento calculado: uma fórmula que devolve itens da coleção de destino — um
               (`entrega.projeto`) ou vários (`tarefas.filter(t, !t._state.final)`) -->
          <ontos-property-form-section v-if="ownerModel.computed" :title="$tt('section.computation')">
            <ontos-formula-input ref="formula" class="required" v-bind="formulaBindings" :value="ownerModel.formula"
                                 :result-label="formulaResultLabel" :draft="draftOf(ownerValue)" :example="computedExample"
                                 @input="ownerModel.formula = $event"/>
          </ontos-property-form-section>
        </template>
      </ontos-property-form-layout>
    </template>

    <!-- As opções permitidas e as regras de cada lado. O dono calculado não as tem — e, calculado, não tem
         espelho: a aba some (`hasValidation`) -->
    <template #validation>
      <ontos-property-form-layout>
        <template #top>
          <div class="relationship-sides">
            <ontos-relationship-side-box v-for="(side, index) in writtenSides" :key="side.key" :idea="side.idea"
                                         :name="side.model.name" :here="index === 0">
              <template v-if="canHaveRulesOn(side.model)">
                <ontos-property-form-section :title="$tt('option-filter')" :explain="$tt('option-filter-explain')">
                  <ontos-option-filter-input ref="optionFilter" :bindings="side.bindings" :draft="side.draft"
                                             :example="side.examples.optionFilter" :value="side.model.optionFilter"
                                             @input="side.model.optionFilter = $event"
                                             :message="side.model.optionFilterMessage"
                                             @message="side.model.optionFilterMessage = $event"/>
                </ontos-property-form-section>
                <ontos-property-form-section :title="$tt('rules')" :explain="$tt('rules-explain')">
                  <ontos-rules-input ref="rules" :bindings="side.bindings" :draft="side.draft"
                                     :example="side.examples.rule" :rules="side.model.rules" @input="side.model.rules = $event"/>
                </ontos-property-form-section>
              </template>
            </ontos-relationship-side-box>
          </div>
        </template>
      </ontos-property-form-layout>
    </template>

    <!-- "Visível quando" e "Editável quando" de cada lado -->
    <template #access>
      <ontos-property-form-layout>
        <template #top>
          <div class="relationship-sides">
            <ontos-relationship-side-box v-for="(side, index) in writtenSides" :key="side.key" :idea="side.idea"
                                         :name="side.model.name" :here="index === 0">
              <ontos-access-input ref="access" v-bind="fieldAccessBindings(side.draft, side.idea, side.current || undefined)"
                                  :value="side.model.access || {}" @input="side.model.access = $event"/>
            </ontos-relationship-side-box>
          </div>
        </template>
      </ontos-property-form-layout>
    </template>
  </ontos-property-tabs>
</template>
<script lang="ts">
import Select from "@/components/shared/Select.vue";
import PairToggle, {PairSide} from "@/components/shared/PairToggle.vue";
import PropertyInput, {accessValue, canHaveRules, optionFilterFields, rulesValue} from "@/components/design/property/PropertyInput";
import {editorsValid} from "@/components/design/property/editors";
import {PropertyAccess} from "@/types/Idea";
import PropertyFormLayout from "@/components/design/property/PropertyFormLayout.vue";
import PropertyFormSection from "@/components/design/property/PropertyFormSection.vue";
import PropertyBehavior, {BehaviorModel, behaviorModelOf, behaviorValue} from "@/components/design/property/PropertyBehavior.vue";
import RelationshipSide, {SideManual} from "@/components/design/property/RelationshipSide.vue";
import RelationshipSideBox from "@/components/design/property/RelationshipSideBox.vue";
import {Component, Prop, toNative} from "vue-facing-decorator";
import Idea from "@/types/Idea";
import IdeaProperty, {PropertyDefault, PropertyRule} from "@/types/IdeaProperty";
import PropertyDefaultInput, {writtenDefault} from "@/components/design/property/PropertyDefault.vue";
import {multipleLabel} from "@/utils/formulaCompletion";
import {mirroredCardinality} from "@/utils/propertyDefinition";
import {deepCopy} from "@/utils/copy";
import {FormulaExamples, formulaExamples} from "@/utils/formulaExamples";
import {t} from "@/i18n";

/** O modelo de UM lado — o dono usa tudo; o espelho, o nome, o alias, as regras e o filtro. */
type SideModel = BehaviorModel & {
  name: string, alias: string, formula: string, rules: PropertyRule[],
  optionFilter?: string, optionFilterMessage?: string, access?: PropertyAccess,
  /** Só o do dono se grava — ver o `PropertyDefault`. */
  default: PropertyDefault | null
}

/** O comportamento que a caixa de um lado mostra — ver o `behaviorOf`. */
type SideBehavior = { model: BehaviorModel, kind: 'relationship' | 'mirror' }

type Side = {
  key: 'owner' | 'mirror', idea: Idea, model: SideModel,
  /** O identificador gravado deste lado, ou vazio no que nasce agora. */
  current: string,
  /** O par Sem/Com espelho: só no painel do espelho, aberto pelo dono. */
  togglable: boolean,
  fields: Record<string, unknown>,
  bindings: Record<string, unknown>, draft: Partial<IdeaProperty>,
  /** Embaixo das fórmulas deste lado, com os nomes da coleção dele. */
  examples: FormulaExamples,
  behavior?: SideBehavior,
  /** O sentido da associação que sai deste lado: "Cada Pedido tem até 1 Cliente". */
  sentence: string
}

const strong = (value: any) => value || undefined

function sideModelOf(property: IdeaProperty | null): SideModel {
  const stored = property || {name: '', alias: ''}
  return {
    name: stored.name || '', alias: stored.alias || '',
    multiple: !!stored.multiple, required: stored.required || false, unique: stored.unique || false,
    identifier: !!stored.identifier,
    //`computed` é flag de tela, derivada da fórmula — o mesmo motivo do atributo
    computed: !!stored.formula, formula: stored.formula || '',
    rules: stored.rules || [],
    //Declaradas, mesmo sem valor: o modelo é observado inteiro, e a chave que falta não seria vista
    optionFilter: stored.optionFilter, optionFilterMessage: stored.optionFilterMessage,
    access: stored.access,
    default: stored.default || null,
    relationship: true,
    ...behaviorModelOf(stored)
  }
}

/**
 * O relacionamento, pelos dois lados: o DONO — o que guarda os ids, com a cardinalidade e o
 * comportamento — e o ESPELHO, na coleção de destino, que a herda invertida. O registro continua o
 * mesmo: duas propriedades, uma em cada coleção, uma apontando para a outra; quem grava as duas numa
 * transação é o servidor (`otherSide`).
 *
 * O `value` é o lado em que a janela foi aberta, e o `other` o de lá: o espelho gravado (ou null), ou o
 * dono, quando se abre pelo espelho. O editor devolve os dois — `input` com este lado, `other` com o
 * de lá, ou null sem espelho.
 *
 * Substitui o editor de relacionamento e o de espelho, que escreviam cada um o nome, o alias e o cálculo
 * do nome — e agora são dois painéis do mesmo `RelationshipSide`.
 */
@Component({emits: ['input', 'other'], components: {
  'ontos-select': Select, 'ontos-pair-toggle': PairToggle,
  'ontos-property-form-layout': PropertyFormLayout, 'ontos-property-form-section': PropertyFormSection,
  'ontos-property-behavior': PropertyBehavior, 'ontos-relationship-side': RelationshipSide,
  'ontos-relationship-side-box': RelationshipSideBox, 'ontos-property-default': PropertyDefaultInput
}})
class RelationshipInput extends PropertyInput {
  @Prop({type: Array, required: true}) readonly ideas!: Idea[]
  @Prop({type: Boolean, default: false}) readonly fixedDestinationClass!: boolean
  /** O outro lado como está gravado: o espelho (null sem ele), ou o dono, aberto pelo espelho. */
  @Prop({type: Object, default: null}) readonly other!: IdeaProperty | null
  /**
   * O outro lado como estava na janela, ao expandir: o espelho ligado, ou `null`, desligado. `undefined`
   * é não ter vindo de janela nenhuma — começa do gravado (`other`). À parte do `other`: o identificador
   * do espelho que nasceu na janela não é fixo, e o gravado diz qual é.
   */
  @Prop({type: Object, default: undefined}) readonly otherStart!: IdeaProperty | null | undefined

  model: SideModel & { relationshipIdea: string } = {...sideModelOf(null), relationshipIdea: ''}
  otherModel: SideModel = sideModelOf(null)
  mirrorOn = false
  destinationError = ''
  /**
   * Os dois lados como abriram. Uma cópia, e não as props: o host devolve cada edição como `value`
   * novo, e o que dependesse dele mudaria a cada eco — com o modelo observado inteiro, sem fim.
   */
  thisOriginal: IdeaProperty = {name: '', alias: ''}
  otherOriginal: IdeaProperty | null = null

  /**
   * No `created`, e não no `mounted`: os painéis montam antes do editor, e é ao montar que o do espelho
   * novo sugere o nome — num modelo que ainda fosse o vazio, a sugestão se perderia.
   */
  created() {
    this.thisOriginal = deepCopy(this.value)
    this.otherOriginal = this.other && deepCopy(this.other)
    this.model = {...sideModelOf(this.value), relationshipIdea: this.value.relationship!.idea}
    //Expandido, o outro lado como estava na janela; senão, o gravado
    const resumed = this.otherStart !== undefined
    this.otherModel = sideModelOf(resumed ? this.otherStart : this.other)
    //Aberto pelo espelho, o dono está sempre lá; aberto pelo dono, há espelho se já havia — e o
    //relacionamento que nasce agora já nasce com ele (pedido do usuário). Criando, o alias não é fixo.
    //Expandido, ligado ou desligado como na janela
    this.mirrorOn = !this.thisIsOwner || (resumed ? !!this.otherStart : !!this.other || !this.fixedAlias)
    //O "Calculado" ligado ainda sem fórmula, que o rascunho não guarda
    if (this.resumed) this.ownerModel.computed = this.resumed.computed
    this.$watch(() => [this.model, this.otherModel, this.mirrorOn], () => {
      this.$emit('input', this.thisIsOwner ? this.ownerValue : this.mirrorValue)
      this.$emit('other', !this.mirrorOn ? null : this.thisIsOwner ? this.mirrorValue : this.ownerValue)
    }, {deep: true})
  }

  get thisIsOwner(): boolean {
    return !this.value.relationship!.reverse
  }

  get ownerModel(): SideModel {
    return this.thisIsOwner ? this.model : this.otherModel
  }

  get mirrorModel(): SideModel {
    return this.thisIsOwner ? this.otherModel : this.model
  }

  /**
   * O comportamento na caixa de cada lado (pedido do usuário): a **Cardinalidade de cada um na caixa
   * dele**, no lugar da Unicidade — "Exclusivo" aqui é exatamente o "Um valor" de lá —, e o resto —
   * obrigatoriedade, compor o nome, calcular — na do dono, que é quem guarda os ids, aberta a janela por
   * qualquer lado.
   *
   * A do espelho é a do dono vista de lá (`mirroredCardinality`), e mudá-la muda o dono: um valor só lá
   * torna o dono exclusivo, e de um valor só, que é como o exclusivo se grava (`behaviorValue`). Sem
   * espelho a caixa de lá também a mostra: o mesmo Gênero em várias Canções, ou não, vale com ou sem o
   * campo de lá. Não no dono calculado, que não é exclusivo — a fórmula escolhe por ele.
   */
  behaviorOf(key: Side['key']): SideBehavior | undefined {
    const owner = this.ownerModel
    if (key === 'owner') return {model: owner, kind: 'relationship'}
    if (owner.computed) return undefined
    const editor = this
    const model = {
      ...owner,
      get multiple() {
        return mirroredCardinality(editor.ownerValue).multiple
      },
      set multiple(multiple: boolean) {
        owner.unique = !multiple
        if (!multiple) owner.multiple = false
      }
    }
    return {model, kind: 'mirror'}
  }

  get ownerOriginal(): IdeaProperty {
    return (this.thisIsOwner ? this.thisOriginal : this.otherOriginal) || {name: '', alias: ''}
  }

  get mirrorOriginal(): IdeaProperty | null {
    return this.thisIsOwner ? this.otherOriginal : this.thisOriginal
  }

  /** O `relationshipIdea` é sempre a coleção de lá — o destino, ou o dono, aberto pelo espelho. */
  get ownerIdeaAlias(): string {
    return this.thisIsOwner ? this.idea.alias : this.model.relationshipIdea
  }

  get mirrorIdeaAlias(): string {
    return this.thisIsOwner ? this.model.relationshipIdea : this.idea.alias
  }

  get ownerIdea(): Idea | undefined {
    return this.ideaWith(this.ownerIdeaAlias)
  }

  get mirrorIdea(): Idea | undefined {
    return this.ideaWith(this.mirrorIdeaAlias)
  }

  ideaWith(alias: string): Idea | undefined {
    return this.ideas.find(candidate => candidate.alias === alias)
  }

  get ownerValue(): IdeaProperty {
    const model = this.ownerModel
    return {
      name: strong(model.name),
      alias: strong(model.alias),
      relationship: {idea: this.mirrorIdeaAlias},
      ...behaviorValue(model),
      formula: model.computed ? strong(model.formula) : undefined,
      default: model.computed ? undefined : writtenDefault(model.default),
      rules: rulesValue(model),
      ...optionFilterFields(model),
      access: accessValue(model.access)
    }
  }

  get mirrorValue(): IdeaProperty {
    const model = this.mirrorModel
    return {
      name: strong(model.name),
      alias: strong(model.alias),
      relationship: {idea: this.ownerIdeaAlias, reverse: this.ownerModel.alias},
      rules: rulesValue(model),
      ...optionFilterFields(model),
      access: accessValue(model.access)
    }
  }

  /** O lado como a checagem da fórmula precisa vê-lo: o destino e o múltiplo dizem o que serve. */
  draftOf(value: IdeaProperty): Partial<IdeaProperty> {
    return {name: value.name, alias: value.alias, relationship: value.relationship, multiple: value.multiple,
      identifier: value.identifier, access: value.access}
  }

  /**
   * Os painéis, este lado primeiro. O de lá só com a coleção escolhida — ao criar, antes do destino,
   * não há onde ele mora.
   */
  get sides(): Side[] {
    const owner = this.sideOf('owner', this.ownerIdea, this.ownerModel,
        this.thisIsOwner ? this.thisCurrent : this.ownerOriginal.alias,
        {idea: this.mirrorIdeaAlias, multiple: this.ownerModel.multiple})
    const mirror = this.sideOf('mirror', this.mirrorIdea, this.mirrorModel,
        this.thisIsOwner ? (this.otherOriginal ? this.otherOriginal.alias : '') : this.thisCurrent,
        //Plural quando o espelho é MÚLTIPLO — a mesma regra da frase da cardinalidade, e não outra
        {idea: this.ownerIdeaAlias, multiple: mirroredCardinality(this.ownerValue).multiple})
    const ordered = this.thisIsOwner ? [owner, mirror] : [mirror, owner]
    return ordered.filter((side): side is Side => !!side)
  }

  get thisCurrent(): string {
    return this.fixedAlias ? this.value.alias : ''
  }

  sideOf(key: Side['key'], idea: Idea | undefined, model: SideModel, current: string, nameSource: object): Side | undefined {
    if (!idea) return undefined
    const draft = this.draftOf(key === 'owner' ? this.ownerValue : this.mirrorValue)
    return {
      key, idea, model, current,
      togglable: key === 'mirror' && this.thisIsOwner,
      fields: {model, currentAlias: current, siblings: this.siblingsOf(key, idea, current),
        nameSource, computeName: this.computeName, sideKey: key,
        manual: (this.resumed && this.resumed.sides[key]) || null},
      bindings: {...this.formulaBindings, idea, current: current || undefined},
      draft,
      examples: formulaExamples(draft, idea, this.workspace),
      behavior: this.behaviorOf(key),
      sentence: this.sentenceOf(key)
    }
  }

  /**
   * "Cada Pedido tem até 1 Cliente", na caixa do Pedido; "Cada Cliente pode ter mais de 1 Pedido", na do
   * Cliente — cada sentido na caixa de onde ele sai (pedido do usuário). Vazio antes do destino.
   */
  sentenceOf(key: Side['key']): string {
    const owner = this.ownerIdea
    const mirror = this.mirrorIdea
    if (!owner || !mirror) return ''
    if (key === 'owner' && this.ownerValue.multiple) return this.countSentence(owner, mirror)
    //Um valor obrigatório: "deve ter 1", e não "tem até 1" (pedido do usuário). O obrigatório só às vezes
    //(a condição) fica no "até"
    if (key === 'owner' && this.ownerValue.required === true)
      return t('relationship.has-exactly-one', {from: owner.name, to: mirror.name})
    const [from, to, multiple] = key === 'owner'
        ? [owner, mirror, false]
        : [mirror, owner, mirroredCardinality(this.ownerValue).multiple]
    return t(multiple ? 'relationship.has-many' : 'relationship.has-one', {from: from.name, to: to.name})
  }

  /**
   * A lista do dono, com o mínimo e o máximo que ela pede (pedido do usuário): "Cada Demanda pode ter até 5
   * Sistemas", "deve ter pelo menos 2", "deve ter de 2 a 5" — e, sem nenhum, "pode ter mais de 1". O
   * destino no plural quando o número pede; com 1, no singular. Pelo `itemCount` que vai gravado, e não
   * pelo que está nos campos: o mínimo 0 e o campo vazio não são limite. O espelho não tem mínimo nem
   * máximo, que são regra de quem preenche.
   */
  countSentence(from: Idea, to: Idea): string {
    const {min, max} = this.ownerValue.itemCount || {}
    const names = (count: number) => count === 1 ? to.name : to.pluralName
    if (min && max) return t('relationship.has-between', {from: from.name, min, max, to: names(max)})
    if (min) return t('relationship.has-at-least', {from: from.name, min, to: names(min)})
    if (max) return t('relationship.has-at-most', {from: from.name, max, to: names(max)})
    return t('relationship.has-many', {from: from.name, to: to.name})
  }

  /** O exemplo da fórmula do dono calculado — que só se calcula aberto pelo dono, sem espelho. */
  get computedExample(): string {
    return formulaExamples(this.draftOf(this.ownerValue), this.idea, this.workspace).computed
  }

  /**
   * Com quem o nome e o identificador de um lado não podem repetir: as propriedades da coleção dele,
   * menos ele mesmo. No relacionamento de uma coleção com ela mesma, os dois lados moram juntos: conta
   * o outro como está na tela, e não como está gravado.
   */
  siblingsOf(key: Side['key'], idea: Idea, current: string): Array<{ name: string, alias: string }> {
    const sameIdea = this.ownerIdeaAlias === this.mirrorIdeaAlias
    const otherKey = key === 'owner' ? 'mirror' : 'owner'
    const otherCurrent = otherKey === 'owner' ? this.ownerOriginal.alias : (this.mirrorOriginal ? this.mirrorOriginal.alias : '')
    const stored = idea.properties.filter(property => property.alias !== current
        && !(sameIdea && property.alias === otherCurrent))
    if (!sameIdea || !this.mirrorOn) return stored
    return [...stored, otherKey === 'owner' ? this.ownerModel : this.mirrorModel]
  }

  /**
   * Os lados que se escrevem — as caixas das abas Validação e Acessos: os que existem como campo, o dono e,
   * ligado, o espelho. Cada um tem "Visível quando" e "Editável quando"; as opções permitidas e as regras,
   * só o que pode tê-las (`canHaveRulesOn` — o dono calculado não).
   */
  get writtenSides(): Side[] {
    return this.sides.filter(side => side.key === 'owner' || this.mirrorOn)
  }

  //Exposta ao template, por lado: o `canHaveRules` da base é o deste lado só
  canHaveRulesOn = canHaveRules

  /** A aba Validação existe se algum lado pode ter opções permitidas e regras. */
  get hasValidation(): boolean {
    return this.writtenSides.some(side => canHaveRules(side.model))
  }

  get mirrorPair(): { label: string, off: PairSide, on: PairSide } {
    return {
      label: this.$tt('pair.mirror') as string,
      off: {label: this.$tt('relationship.mirror-off') as string, hint: this.$tt('relationship.mirror-off-hint') as string},
      on: {label: this.$tt('relationship.mirror-on') as string, hint: this.$tt('relationship.mirror-on-hint') as string,
        icon: 'mdi-arrow-left-right', color: 'primary'}
    }
  }

  /** O que a fórmula dá: itens da coleção de destino — um, ou vários no múltiplo. */
  get formulaResultLabel(): string {
    const target = this.mirrorIdea
    return multipleLabel(target ? target.name : this.model.relationshipIdea, this.ownerModel.multiple)
  }

  /** O nome de um lado nasce da coleção para onde ele aponta: no plural quando aponta para vários. */
  computeName(source: { idea?: string, multiple: boolean }): string {
    const target = source.idea ? this.ideaWith(source.idea) : undefined
    if (!target) return ''
    return source.multiple ? target.pluralName : target.name
  }

  /**
   * O que levar ao expandir, além dos dois rascunhos: o "Calculado" ligado ainda sem fórmula e, em cada
   * painel, o nome e o identificador digitados à mão.
   */
  snapshot(): { computed: boolean, sides: Record<string, SideManual> } {
    const sides = ([this.$refs.sides].flat() as Array<{ sideKey: string, manualFlags(): SideManual } | undefined>)
        .filter((side): side is { sideKey: string, manualFlags(): SideManual } => !!side)
    return {computed: !!this.ownerModel.computed,
      sides: Object.fromEntries(sides.map(side => [side.sideKey, side.manualFlags()]))}
  }

  /**
   * A aba Propriedade: o destino, os dois painéis e a fórmula do calculado. O nome e o alias são dos
   * painéis — cada um contra a própria coleção —, e não do `validateName` da base, que só conhece esta.
   */
  validProperty(): boolean {
    this.destinationError = this.model.relationshipIdea ? '' : this.$tt('required-message') as string
    return [!this.destinationError, editorsValid(this.$refs.sides), this.validEditors()].every(Boolean)
  }
}

export default toNative(RelationshipInput)
</script>

<style scoped>
/* As caixas lado a lado, uma embaixo da outra quando não cabem duas: o comportamento do dono — os pares
   de dois lados — pede mais que os 240px de quando a caixa só tinha o nome */
.relationship-sides {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}
</style>
